import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Input,
  Spinner,
  IconButton,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useVerifyOtp, useVerifyResetOtp } from '../utils/auth.api';

export default function VerifyOtpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mutateAsync: verifyResetOtp, isLoading } = useVerifyResetOtp(); 
  console.log("verifyResetOtp..", verifyResetOtp)
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [timer, setTimer] = useState(60); // 60 seconds countdown
  const passedUserName = location.state?.email || '';
  
  // Refs for OTP inputs
  const inputRefs = useRef([]);

  // Timer effect
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  // Format timer display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;
    
    const digit = value.slice(-1); // Keep only last numeric char
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);

    // Auto-focus next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (index, e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!pastedData) return;

    const newOtp = [...otp];
    let lastFilledIndex = index;

    for (let i = 0; i < pastedData.length && index + i < 6; i++) {
      newOtp[index + i] = pastedData[i];
      lastFilledIndex = index + i;
    }

    setOtp(newOtp);
    inputRefs.current[Math.min(lastFilledIndex + 1, 5)]?.focus();
  };

  // Resend OTP
  const handleResend = () => {
    setTimer(60);
    setOtp(['', '', '', '', '', '']);
    toast.info('OTP resent successfully');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const otpCode = otp.join('');
    
    if (!otpCode || otpCode.length !== 6) {
      toast.warning('Please enter complete 6-digit OTP');
      return;
    }

    try {
      const response = await verifyResetOtp({ 
        email: passedUserName, 
        otp: otpCode
      });

      console.log("otp response..", response)
      
      // Only navigate if verification is successful
      if (response && response.message === "OTP verified") {
        toast.success('OTP Verified Successfully');
        navigate('/reset-password', { 
          state: { 
            email: passedUserName, 
            otp: otpCode 
          } 
        });
      } else {
        toast.error('Invalid OTP. Please try again.');
      }

    } catch (err) {
      console.error('Error:', err);
      toast.error(`Failed to verify: ${err?.message}`);
    }
  };

  return (
    <Box minH="100vh" bg="white">
      <Container maxW={{ base: "full", sm: "sm" }} centerContent px={{ base: 4, sm: 0 }}>
        <VStack spacing={{ base: 6, sm: 8 }} w="100%" pt={{ base: 6, sm: 8 }}>
          {/* Header with back button */}
          <HStack w="100%" justify="space-between" align="center">
            <IconButton
              icon={<ArrowBackIcon />}
              variant="ghost"
              onClick={() => navigate(-1)}
              aria-label="Go back"
              size={{ base: "sm", sm: "md" }}
            />
            <Text fontWeight="semibold" fontSize={{ base: "md", sm: "lg" }}>OTP</Text>
            <Box w={{ base: "32px", sm: "40px" }} /> {/* Spacer for centering */}
          </HStack>

          {/* Title Section */}
          <Box textAlign="center" w="100%" px={{ base: 2, sm: 0 }}>
            <Heading as="h1" size={{ base: "md", sm: "lg" }} color="gray.900" fontWeight="bold" mb={3}>
              Email verification
            </Heading>
            <Text color="gray.500" fontSize={{ base: "xs", sm: "sm" }} textAlign="center">
              Enter the verification code we send you on:
            </Text>
            <Text color="gray.500" fontSize={{ base: "xs", sm: "sm" }} fontWeight="medium">
              {passedUserName}
            </Text>
          </Box>

          {/* OTP Input Section */}
          <VStack spacing={6} w="100%">
            <HStack 
              spacing={{ base: 2, sm: 3, md: 4 }} 
              justify="center"
              w="100%"
              px={{ base: 4, sm: 0 }}
            >
              {otp.map((digit, index) => (
                <Input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={(e) => handlePaste(index, e)}
                  maxLength={1}
                  textAlign="center"
                  fontSize={{ base: "xl", sm: "2xl", md: "2xl" }}
                  fontWeight="bold"
                  w={{ base: "48px", sm: "52px", md: "56px" }}
                  h={{ base: "48px", sm: "52px", md: "56px" }}
                  minW={{ base: "48px", sm: "52px", md: "56px" }}
                  p={0}
                  bg="gray.50"
                  borderColor="gray.200"
                  borderRadius="lg"
                  _focus={{ borderColor: "#5A5BED", boxShadow: "0 0 0 1px #5A5BED" }}
                  inputMode="numeric" // Shows number keyboard on mobile
                  pattern="[0-9]*" // Additional hint for numeric input
                  lineHeight="1"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                />
              ))}
            </HStack>

            {/* Resend Section */}
            <VStack spacing={2}>
              <Text fontSize="sm" color="gray.500">
                Didn't receive code?{' '}
                {timer > 0 ? (
                  <Text as="span" color="gray.400">
                    Resend in {formatTime(timer)}
                  </Text>
                ) : (
                  <Text 
                    as="span" 
                    color="#5A5BED" 
                    cursor="pointer" 
                    fontWeight="medium"
                    onClick={handleResend}
                  >
                    Resend
                  </Text>
                )}
              </Text>
            </VStack>
          </VStack>

          {/* Password Fields (if needed for your flow) */}
          <VStack spacing={4} w="100%" display="none">
            <Input
              type="password"
              placeholder="Enter New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              size="lg"
              bg="gray.50"
              borderColor="gray.200"
              borderRadius="lg"
            />
            <Input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              size="lg"
              bg="gray.50"
              borderColor="gray.200"
              borderRadius="lg"
            />
          </VStack>

          {/* Continue Button */}
          <Button
            w="100%"
            bg="#5A5BED"
            height="50px"
            color="white"
            fontWeight="semibold"
            borderRadius="100px"
            isLoading={isLoading}
            loadingText="Verifying..."
            spinner={<Spinner size="sm" color="white" />}
            _hover={{ bg: "#4A4BDD" }}
            onClick={handleSubmit}
            mt={{ base: 8, md: 4 }}
            isDisabled={otp.join('').length !== 6}
          >
            Continue
          </Button>
        </VStack>
      </Container>
    </Box>
  );
}